import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/cn';

/**
 * IconButton — square icon-only button for navbar actions, toolbars,
 * overlay controls. Matches Agent Builder's navbar icon-button pattern:
 *   rounded-lg p-2 hover:bg-gray-800 (dark) / hover:bg-gray-100 (light)
 */
interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    icon: ReactNode;
    label: string; // aria-label / title
    isDarkMode?: boolean;
    variant?: 'default' | 'accent';
    size?: 'sm' | 'md';
}

const SIZE = {
    sm: 'p-1.5 rounded-md',
    md: 'p-2 rounded-lg',
};

function variantClass(variant: 'default' | 'accent', isDark: boolean): string {
    if (variant === 'accent') {
        return isDark
            ? 'text-blue-400 hover:bg-gray-800'
            : 'text-blue-600 hover:bg-blue-50';
    }
    return isDark
        ? 'text-gray-300 hover:bg-gray-800 hover:text-gray-100'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900';
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
    { icon, label, isDarkMode = false, variant = 'default', size = 'md', className, ...rest },
    ref,
) {
    return (
        <button
            ref={ref}
            type="button"
            aria-label={label}
            title={label}
            className={cn(
                'inline-flex items-center justify-center transition-colors duration-150',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                SIZE[size],
                variantClass(variant, isDarkMode),
                className,
            )}
            {...rest}
        >
            {icon}
        </button>
    );
});

export default IconButton;
